<div class="detail-page">
  <div class="header">
    <app-back-button
      parentRoute="/admin/process-management/code-catalog"
      [useHistoryBack]="true"
      [title]="title()"
    ></app-back-button>
  </div>

  <app-loading *ngIf="loading()"></app-loading>
  <div *ngIf="error() as message" class="error">{{ message }}</div>
  <div *ngIf="!loading() && notFound()" class="error">{{ 'Command not found in the code catalog.' | translate }}</div>

  <ng-container *ngIf="command() as command">
    <header class="title-block">
      <h1>
        {{ command.name }}
        <app-catalog-authors [authors]="command.authors"></app-catalog-authors>
      </h1>
      <p class="muted">
        <p-tag
          [severity]="catalogKindSeverity(command.kind)"
          [value]="command.kind || undefined"
        ></p-tag>
        <p-tag
          *ngIf="!isCqrsCompliant(command)"
          severity="danger"
          value="cqrs"
          [pTooltip]="cqrsIssuesTooltip(command)"
          tooltipPosition="top"
        ></p-tag>
        <p-tag
          *ngIf="!isPlacementCompliant(command)"
          severity="danger"
          value="placement"
          [pTooltip]="placementIssuesTooltip(command)"
          tooltipPosition="top"
        ></p-tag>
        <p-tag
          *ngIf="isCatalogItemObsolete(command)"
          severity="secondary"
          value="obsolete"
          pTooltip="Retired: marked [Obsolete]"
          tooltipPosition="top"
        ></p-tag>
        · {{ catalogGroupLabel(command.group) }} · {{ command.fullName || command.sourcePath }}
        · {{ 'Refs' | translate }}: {{ commandReferenceCount(command) }}
      </p>
      <p-tag *ngIf="command.discoveredAtRuntime" severity="warn" value="runtime"></p-tag>
    </header>

    <section>
      <h2>{{ 'Description' | translate }}</h2>
      <p>{{ (command | serverTranslate:'descriptions') || command.description || ('No XML summary found on the command type.' | translate) }}</p>
    </section>

    <section *ngIf="!isCqrsCompliant(command)">
      <h2>{{ 'CQRS issues' | translate }}</h2>
      <ul>
        <li *ngFor="let issue of command.cqrsIssues || []">{{ issue }}</li>
      </ul>
    </section>

    <section *ngIf="!isPlacementCompliant(command)">
      <h2>{{ 'Placement issues' | translate }}</h2>
      <ul>
        <li *ngFor="let issue of command.placementIssues || []">{{ issue }}</li>
      </ul>
    </section>

    <section>
      <h2>{{ 'External services' | translate }}</h2>
      <div class="external-services" *ngIf="command.externalServices?.length; else noServices">
        <p-tag
          *ngFor="let service of command.externalServices"
          severity="secondary"
          [value]="service.name || undefined"
          [pTooltip]="service.project + (service.entryPoint ? ' · ' + service.entryPoint : '')"
          tooltipPosition="top"
        ></p-tag>
      </div>
      <ng-template #noServices>
        <p class="muted">{{ 'No external services detected on the handler' | translate }}</p>
      </ng-template>
    </section>

    <section>
      <h2>{{ 'Configuration' | translate }} ({{ command.configurationSections?.length || 0 }})</h2>
      <div class="config-sections" *ngIf="command.configurationSections?.length; else noConfig">
        <details *ngFor="let section of command.configurationSections" class="config-section">
          <summary>
            <code>{{ section.section }}</code>
            <span class="muted" *ngIf="section.optionsType"> · {{ section.optionsType }}</span>
            <span class="muted" *ngIf="section.source"> · {{ section.source }}</span>
          </summary>
          <pre *ngIf="section.currentConfigJson; else noLiveConfig"><code>{{ formatJson(section.currentConfigJson) }}</code></pre>
          <ng-template #noLiveConfig>
            <p class="muted">{{ 'Live values unavailable for this section' | translate }}</p>
          </ng-template>
        </details>
      </div>
      <ng-template #noConfig>
        <p class="muted">{{ 'No IOptions / appsettings sections detected' | translate }}</p>
      </ng-template>
    </section>

    <section>
      <h2>{{ 'Parameters' | translate }}</h2>
      <ul class="type-list">
        <li *ngFor="let parameter of command.parameters || []">
          <div>
            <code>{{ parameter.name }}</code>:
            <span class="type-name">{{ parameter.type }}</span>
            <span class="muted" *ngIf="parameter.typeKind && parameter.typeKind !== 'unknown'">
              · {{ parameter.typeKind }}<span *ngIf="parameter.elementType"> of {{ parameter.elementType }}</span>
            </span>
          </div>
          <ul class="type-members" *ngIf="parameter.properties?.length">
            <li *ngFor="let member of parameter.properties">
              <code>{{ member.name }}</code>: {{ member.type }}
            </li>
          </ul>
        </li>
        <li *ngIf="!(command.parameters?.length)" class="muted">{{ 'No parameters' | translate }}</li>
      </ul>
    </section>

    <section>
      <h2>{{ 'Returns' | translate }}</h2>
      <p>
        <app-copyable-identifier [value]="command.responseType || ''"></app-copyable-identifier>
        <span class="muted" *ngIf="command.responseTypeShape?.kind">
          · {{ command.responseTypeShape?.kind }}
          <span *ngIf="command.responseTypeShape?.elementType">
            of {{ command.responseTypeShape?.elementType }}
          </span>
        </span>
      </p>
      <ul class="type-members" *ngIf="command.responseTypeShape?.properties?.length">
        <li *ngFor="let member of command.responseTypeShape?.properties || []">
          <code>{{ member.name }}</code>: {{ member.type }}
        </li>
      </ul>
    </section>

    <section>
      <h2>{{ 'Referenced by' | translate }} ({{ commandReferenceCount(command) }})</h2>
      <ul>
        <li *ngFor="let source of commandReferenceSources(command)">
          <p-tag [value]="source.kind || undefined"></p-tag>
          <ng-container [ngSwitch]="source.kind">
            <app-copyable-identifier *ngSwitchCase="'Job'" [value]="source.name || ''" [link]="jobLink(source.name)"></app-copyable-identifier>
            <app-copyable-identifier *ngSwitchCase="'Command'" [value]="source.name || ''" [link]="commandLink(source.name)"></app-copyable-identifier>
            <span *ngSwitchDefault>{{ source.name }}</span>
          </ng-container>
          <span *ngIf="source.httpMethods?.length"> ({{ source.httpMethods?.join(', ') }})</span>
          <div class="muted" *ngIf="source.path">{{ source.path }}</div>
        </li>
        <li *ngIf="!commandReferenceSources(command).length" class="muted">{{ 'No inbound references' | translate }}</li>
      </ul>
    </section>

    <section>
      <h2>{{ 'Used by jobs' | translate }}</h2>
      <ul>
        <li *ngFor="let job of command.usedByJobs || []">
          <app-copyable-identifier [value]="job" [link]="jobLink(job)"></app-copyable-identifier>
        </li>
        <li *ngIf="!(command.usedByJobs?.length)" class="muted">{{ 'Not referenced by Quartz jobs' | translate }}</li>
      </ul>
    </section>

    <section>
      <h2>{{ 'Command chain' | translate }}</h2>
      <ul>
        <li *ngFor="let chained of command.chainedCommands || []">
          <app-copyable-identifier [value]="chained" [link]="commandLink(chained)"></app-copyable-identifier>
        </li>
        <li *ngIf="!(command.chainedCommands?.length)" class="muted">{{ 'No nested MediatR sends detected' | translate }}</li>
      </ul>
    </section>

    <section>
      <h2>{{ 'Referenced by commands' | translate }}</h2>
      <ul>
        <li *ngFor="let parent of command.referencedByCommands || []">
          <app-copyable-identifier [value]="parent" [link]="commandLink(parent)"></app-copyable-identifier>
        </li>
        <li *ngIf="!(command.referencedByCommands?.length)" class="muted">
          {{ 'No parent MediatR handlers send this command' | translate }}
        </li>
      </ul>
    </section>

    <section>
      <h2>{{ 'Callers' | translate }}</h2>
      <ul>
        <li *ngFor="let caller of command.callers || []">
          <p-tag [value]="caller.kind || undefined"></p-tag>
          <ng-container *ngIf="caller.kind === 'Job'; else plainCaller">
            <app-copyable-identifier [value]="caller.name || ''" [link]="jobLink(caller.name || '')"></app-copyable-identifier>
          </ng-container>
          <ng-template #plainCaller>{{ caller.name }}</ng-template>
          <span *ngIf="caller.httpMethods?.length"> ({{ caller.httpMethods?.join(', ') }})</span>
          <div class="muted">{{ caller.path }}</div>
        </li>
        <li *ngIf="!(command.callers?.length)" class="muted">
          {{ 'No API/job callers indexed yet. Regenerate the catalog if this is unexpected.' | translate }}
        </li>
      </ul>
    </section>
  </ng-container>
</div>
